import { useEffect, useState } from "react";
import { Mail, Send, ShieldCheck, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

// The staff member's OWN Microsoft 365 mailbox: messages leave as them, never from a shared account.
export function MailboxCard({ status, onChanged }) {
  const connect = async () => {
    try {
      const { data } = await api.get("/communications/mailbox/authorize-url");
      window.location.assign(data.authorize_url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Microsoft 365 sign-in is not available"); }
  };
  const disconnect = async () => { await api.post("/communications/mailbox/disconnect"); onChanged(); };
  const runNow = async () => {
    try { const { data } = await api.post("/communications/dispatch/run"); toast.success(`Dispatch pass: ${JSON.stringify(data.processed)}`); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Dispatch is not available"); }
  };
  if (!status) return null;
  return (
    <div data-testid="mailbox-card" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-jigsaw-border bg-white px-4 py-3 text-sm">
      <div className="flex items-center gap-2">
        <Mail className="h-4 w-4 text-jigsaw-navy" />
        {status.connected ? (
          <span className="text-jigsaw-navy">Sending as <span className="font-semibold">{status.mailbox_address}</span> (your delegated mailbox)</span>
        ) : (
          <span className="text-jigsaw-muted">
            {status.graph_enabled ? "Connect your Microsoft 365 mailbox to queue e-mail." : "Microsoft 365 is not enabled in this environment - drafts can be prepared and reviewed, nothing can be sent."}
            {status.status === "REVOKED" && ` Connection revoked: ${status.last_error || "reconnect required"}.`}
          </span>
        )}
      </div>
      <div className="flex gap-2">
        {status.graph_enabled && !status.connected && <Button data-testid="mailbox-connect" size="sm" onClick={connect}>Connect mailbox</Button>}
        {status.connected && <Button data-testid="mailbox-disconnect" size="sm" variant="outline" onClick={disconnect}>Disconnect</Button>}
        {status.graph_enabled && !status.worker_enabled && <Button data-testid="dispatch-run" size="sm" variant="outline" onClick={runNow}><RefreshCw className="mr-1 h-3.5 w-3.5" />Run dispatch now</Button>}
      </div>
    </div>
  );
}

// Recipient must be one of the organisation's contacts - the server enforces the same rule.
export function RecipientSelect({ entityType, entityId, value, onChange }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!entityType || !entityId) { setItems(null); return; }
    api.get("/communications/recipients", { params: { entity_type: entityType, entity_id: entityId } }).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, [entityType, entityId]);
  return (
    <div>
      <Label>Recipient (organisation contacts only)</Label>
      <Select value={value || ""} onValueChange={onChange} disabled={!items || items.length === 0}>
        <SelectTrigger data-testid="recipient-select"><SelectValue placeholder={items === null ? "Enter the record ID first" : items.length ? "Choose a contact" : "No contact with an e-mail address on this organisation"} /></SelectTrigger>
        <SelectContent>{(items || []).map((i) => <SelectItem key={i.address} value={i.address}>{i.name} · {i.address}{i.is_primary ? " (primary)" : ""}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

export function CommunicationActions({ c, mailbox, onChanged }) {
  const act = async (path, body) => {
    try { const { data } = await api.post(`/communications/${c.id}/${path}`, body); toast.success(data.note || `Now ${data.status}`); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  if (c.status === "DRAFT") return <Button data-testid={`review-communication-${c.id}`} size="sm" variant="outline" onClick={() => act("review")}><ShieldCheck className="mr-1 h-3.5 w-3.5" />Mark reviewed</Button>;
  if (c.status === "REVIEWED") return (
    <Button data-testid={`queue-communication-${c.id}`} size="sm" disabled={!mailbox?.connected} title={mailbox?.connected ? "" : "Connect your mailbox first"} onClick={() => act("queue")}>
      <Send className="mr-1 h-3.5 w-3.5" />Queue from my mailbox
    </Button>
  );
  if (c.status === "UNKNOWN" || c.status === "DEAD") return (
    <div className="flex gap-1">
      <Button data-testid={`resolve-requeue-${c.id}`} size="sm" variant="outline" onClick={() => act("resolve", { resolution: "REQUEUE" })}>Re-queue</Button>
      <Button data-testid={`resolve-failed-${c.id}`} size="sm" variant="outline" className="text-jigsaw-danger" onClick={() => act("resolve", { resolution: "MARK_FAILED" })}>Mark failed</Button>
      {c.status === "UNKNOWN" && <Button data-testid={`resolve-accepted-${c.id}`} size="sm" variant="outline" onClick={() => act("resolve", { resolution: "MARK_ACCEPTED", notes: "Found in Sent Items" })}>Found in Sent Items</Button>}
    </div>
  );
  return null;
}
